// src/hooks/useDialogDocStatus.js

import { useMemo } from 'react';
import { useProjectContext } from '../context/ProjectContext';
import { useChatContext } from '../context/ChatContext';

/**
 * Custom hook to determine if the current dialog has no documents or 
 * has documents that are still being processed.
 * 
 * This hook derives document status directly from project data and 
 * dialog KB IDs without making additional API calls.
 * 
 * @returns {Object} { noDocs, anyInProgress, loading }
 */
export default function useDialogDocStatus() {
  const { projects, loading: projectsLoading } = useProjectContext();
  const { dialogKbIds } = useChatContext();

  // Derive noDocs & anyInProgress purely from local state
  const { noDocs, anyInProgress } = useMemo(() => {
    // If projects are still loading, return neutral state
    if (projectsLoading) {
      return { noDocs: false, anyInProgress: false };
    }

    // If no projects exist yet, return noDocs=true
    if (!projects || projects.length === 0) {
      return { noDocs: true, anyInProgress: false };
    }

    // If no kbIds, that means the dialog truly has none
    if (!dialogKbIds || dialogKbIds.length === 0) {
      return { noDocs: true, anyInProgress: false };
    }

    // Collect all documents from these KBs across all projects
    let allDocs = [];
    let hasValidKb = false;

    for (const project of projects) {
      for (const phase of project.phases) {
        // Check if this phase's KB is one we're interested in
        if (phase.kb_id && dialogKbIds.includes(phase.kb_id)) {
          hasValidKb = true;
          if (phase.files && Array.isArray(phase.files)) {
            allDocs = allDocs.concat(phase.files);
          }
        }
      }
    }

    // If we found no valid KBs, return noDocs=true
    if (!hasValidKb) {
      return { noDocs: true, anyInProgress: false };
    }

    // If no docs found in valid KBs, return noDocs=true
    if (allDocs.length === 0) {
      return { noDocs: true, anyInProgress: false };
    }

    // Check if any documents are still being processed
    // A document is being processed if run="1" and progress < 1.0
    const anyDocInProgress = allDocs.some(doc => {
      if (!doc) return false;
      const runValue = String(doc.run || "1");
      const progress = parseFloat(doc.progress || 0);
      const isInProgress = runValue === "1" && (isNaN(progress) || progress < 1.0);
      
      // Debug logging
      console.log('[DocStatus] Document:', doc.name, 'run:', runValue, 'progress:', progress, 'isInProgress:', isInProgress);
      
      return isInProgress;
    });

    console.log('[DocStatus] Found', allDocs.length, 'documents, anyInProgress:', anyDocInProgress);

    return { noDocs: false, anyInProgress: anyDocInProgress };
  }, [dialogKbIds, projects, projectsLoading]);

  return { 
    noDocs, 
    anyInProgress,
    loading: projectsLoading
  };
} 